import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  User,
  Mail,
  Phone,
  Shield,
  Bell,
  Building2,
  Save,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export default function SettingsPage() {
  const { user, setUser, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  
  const [companyData, setCompanyData] = useState({
    company_name: 'Omaz Consulting',
    address: '64 résidence Elysée II',
    city: '78170 La Celle Saint Cloud',
    country: 'France',
    siret: '89236498500010',
    vat_number: 'FR18892364985',
    iban: 'FR76 XXXX XXXX XXXX XXXX XXXX XXX',
    bic: 'BNPAFRPP',
    email: 'contact@omaz-consulting.com',
    phone: '+33 1 XX XX XX XX',
  });
  
  const [emailStatus, setEmailStatus] = useState({ enabled: false, message: '' });

  useEffect(() => {
    if (isAdmin) {
      fetchCompanySettings();
      fetchEmailStatus();
    }
  }, [isAdmin]);

  const fetchCompanySettings = async () => {
    try {
      const response = await api.get('/settings/company');
      setCompanyData(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const fetchEmailStatus = async () => {
    try {
      const response = await api.get('/settings/email-status');
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Error fetching email status:', error);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      toast.success('Profil mis à jour');
      setUser({ ...user, ...profileData });
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async () => {
    setLoading(true);
    try {
      await api.put('/settings/company', companyData);
      toast.success('Paramètres entreprise mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrateur',
      manager: 'Manager',
      consultant: 'Consultant',
    };
    return labels[role] || role;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Paramètres</h1>
          <p className="text-slate-500 mt-1">
            Gérez votre compte et vos préférences
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile" data-testid="settings-profile-tab">
              <User className="w-4 h-4 mr-2" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-notifications-tab">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="company" data-testid="settings-company-tab">
                <Building2 className="w-4 h-4 mr-2" />
                Entreprise
              </TabsTrigger>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Informations personnelles</CardTitle>
                <CardDescription>
                  Mettez à jour vos informations de profil
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar section */}
                <div className="flex items-center gap-6">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-slate-900 text-white text-xl">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-900">{user?.name}</h3>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                    <Badge className="mt-2" variant="secondary">
                      <Shield className="w-3 h-3 mr-1" />
                      {getRoleLabel(user?.role)}
                    </Badge>
                  </div>
                </div>

                <Separator />

                {/* Form */}
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="name"
                          value={profileData.name}
                          onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                          className="pl-10"
                          data-testid="settings-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          className="pl-10"
                          disabled
                          data-testid="settings-email-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="phone"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder="+33 6 12 34 56 78"
                        className="pl-10"
                        data-testid="settings-phone-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveProfile} 
                    disabled={loading}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="save-profile-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Préférences de notifications</CardTitle>
                <CardDescription>
                  Configurez comment vous souhaitez être notifié
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Notifications in-app</p>
                    <p className="text-sm text-slate-500">Recevoir des notifications dans l'application</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Activé
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Notifications par email</p>
                    <p className="text-sm text-slate-500">Recevoir des résumés par email</p>
                  </div>
                  {emailStatus.enabled ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Configuré
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">
                      <XCircle className="w-3 h-3 mr-1" />
                      Non configuré
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">CRA en attente</p>
                    <p className="text-sm text-slate-500">Être notifié des CRA à valider</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Activé
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Nouvelles missions</p>
                    <p className="text-sm text-slate-500">Être notifié des nouvelles affectations</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Activé
                  </Badge>
                </div>
                
                {!emailStatus.enabled && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Configuration SendGrid requise:</strong> Pour activer les notifications email, 
                      ajoutez votre clé API SendGrid dans les variables d'environnement (SENDGRID_API_KEY).
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="company">
              <Card>
                <CardHeader>
                  <CardTitle>Informations entreprise</CardTitle>
                  <CardDescription>
                    Ces informations apparaissent sur vos factures
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">OC</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-slate-900">{companyData.company_name}</h3>
                      <p className="text-slate-500">Entreprise de Services du Numérique</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Raison sociale</Label>
                        <Input 
                          value={companyData.company_name} 
                          onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                          data-testid="company-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input 
                          value={companyData.email} 
                          onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                          data-testid="company-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Adresse</Label>
                      <Input 
                        value={companyData.address} 
                        onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })}
                        data-testid="company-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Ville</Label>
                        <Input 
                          value={companyData.city} 
                          onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                          data-testid="company-city-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Téléphone</Label>
                        <Input 
                          value={companyData.phone} 
                          onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })}
                          data-testid="company-phone-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>SIRET</Label>
                        <Input 
                          value={companyData.siret} 
                          onChange={(e) => setCompanyData({ ...companyData, siret: e.target.value })}
                          data-testid="company-siret-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>N° TVA Intracommunautaire</Label>
                        <Input 
                          value={companyData.vat_number} 
                          onChange={(e) => setCompanyData({ ...companyData, vat_number: e.target.value })}
                          data-testid="company-vat-input"
                        />
                      </div>
                    </div>
                    
                    <Separator />
                    <p className="text-sm font-medium text-slate-700">Coordonnées bancaires</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>IBAN</Label>
                        <Input 
                          value={companyData.iban} 
                          onChange={(e) => setCompanyData({ ...companyData, iban: e.target.value })}
                          data-testid="company-iban-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>BIC</Label>
                        <Input 
                          value={companyData.bic} 
                          onChange={(e) => setCompanyData({ ...companyData, bic: e.target.value })}
                          data-testid="company-bic-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      onClick={handleSaveCompany}
                      disabled={loading}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="save-company-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
